// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useNavTabs from "@/hooks/useNavTabs";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import StoreSwitcher from "@/UI/components/StoreSwitcher";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Everything the bottom bar could not fit. It lists every destination, not just the overflow, so a
 * thumb that reaches for "More" never has to remember which four made the cut — which also makes it
 * the phone's stand-in for the sidebar, store switcher included.
 */
export default function MoreTabsPopup({ onClose }: Props) {
    const t = useTranslation();
    const { allTabs } = useNavTabs();
    const storeId = useStore((state) => state.storeId);
    const openedStoreId = useRef(storeId);

    /** Switching store rebuilds the whole dashboard behind this sheet — leaving it open strands the user. */
    useEffect(() => {
        if (storeId !== openedStoreId.current) onClose();
    }, [storeId]);

    return (
        <div className="more-tabs">
            <div className="more-tabs-header">
                <StoreSwitcher />
                <button type="button" className="more-tabs-close" onClick={onClose} aria-label={t.bottomBar.close}>
                    <img src="/icons/close-icon.svg" alt="" />
                </button>
            </div>

            <nav className="more-tabs-nav">
                {allTabs.map((tab) => (
                    <NavLink
                        key={tab.path}
                        to={tab.path}
                        end={tab.path === "/"}
                        onClick={onClose}
                        className={({ isActive }) => `more-tabs-btn${isActive ? " active" : ""}`}
                    >
                        <img className="more-tabs-btn-icon more-tabs-btn-icon--dark" src={`/icons/side-bar/${tab.icon}-icon-dark.svg`} alt="" />
                        <img className="more-tabs-btn-icon more-tabs-btn-icon--white" src={`/icons/side-bar/${tab.icon}-icon-white.svg`} alt="" />
                        <span className="more-tabs-btn-label">{tab.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* The sheet covers the backdrop edge to edge, so tapping outside is not an option. This
                sits where the button that opened it was — the same thumb closes it without moving. */}
            <div className="more-tabs-footer">
                <button type="button" className="more-tabs-dismiss" onClick={onClose}>
                    <img src="/icons/close-icon.svg" alt="" width={14} height={14} />
                    <span>{t.bottomBar.close}</span>
                </button>
            </div>
        </div>
    );
}
